import { action, makeObservable, observable, IObservableArray } from 'mobx';
import { AxiosResponse } from 'axios';
import { plainToInstance } from 'class-transformer';
import {
    PayPalButtons,
    PayPalButtonsComponentProps,
    PayPalScriptProvider,
    ReactPayPalScriptOptions,
} from '@paypal/react-paypal-js';
import { api } from '~/tools/api/api';
import moment from 'moment';
import ReactGA from 'react-ga4';
import { EApiUrl } from '~/tools/api/enums';
import { ModelWdollarPackage } from '~/models/model.wdollar.package';
import { PaymentType } from '~/services/service.enums';
import { OfferItem } from '~/views/types';
import { GaCategories, GaEvents } from '~/views/enums';
import { extractServerError } from '~/tools/error';

export class PaymentService {
    @observable public paymentMethod: string = PaymentType.creditCard;
    @observable public showSuccessBlock = false;
    @observable public showAllMode = true;
    @observable public slideArrowLeftEnabled = false;
    @observable public slideArrowRightEnabled = true;
    @observable public selectedWdollarPackage: ModelWdollarPackage;
    @observable public lastError: { description: string; code?: string } = { description: '', code: undefined };

    public paypalClientId: string = process.env.PAYPAL_CLIENT_ID ? process.env.PAYPAL_CLIENT_ID : '';
    public wdollarPackages: IObservableArray<ModelWdollarPackage> = observable.array([]);
    public order: any;

    constructor() {
        makeObservable(this);
    }

    @action
    handleViewAll = () => {
        if (this.showAllMode) {
            ReactGA.event({
                category: GaCategories.purchaseFlow,
                action: GaEvents.allOffersClicked,
                label: JSON.stringify({
                    total_offers_revealed: this.wdollarPackages.length,
                }),
            });
        }

        this.showAllMode = !this.showAllMode;
    };

    @action
    handleSwitchPayment = (e: any) => {
        e.preventDefault();
        //this.paymentMethod = this.paymentMethod === PaymentType.creditCard ? PaymentType.paypal : PaymentType.creditCard;
    };

    @action
    setShowSuccessBlock = (value: boolean) => {
        this.showSuccessBlock = value;
    };

    @action
    setWdollarPackages = (wdollarPackages: any) => {
        this.wdollarPackages.replace(wdollarPackages);
    };

    @action
    getWdollarPackages = async (authService: any) => {
        try {
            const response: AxiosResponse = await api.get<ModelWdollarPackage[]>(EApiUrl.wdollarPackages, {
                headers: {
                    Authorization: authService.currentUser.token,
                },
            });
            if (response.status === 200) {
                const packages = [];
                for (const item of response.data.bundles[0].offers) {
                    const wdollar = (item.items as OfferItem[]).find((record: OfferItem) => record.typ === 'wdollar');
                    if (wdollar && wdollar.parameters) {
                        const wdollarParams = JSON.parse(wdollar.parameters);
                        let freePlayAmount = 0;

                        const freePlayObj = item.items.find((record: OfferItem) => record.typ === 'free_play');
                        if (freePlayObj && freePlayObj.parameters) {
                            const freePlayParams = JSON.parse(freePlayObj.parameters);
                            freePlayAmount = freePlayParams.amount;
                        }

                        const price: number = +item.amount;
                        const discount = Math.round(((+wdollarParams.amount - price) / price) * 100);
                        const discountAmount = Math.round(+wdollarParams.amount - price);
                        let discountText: string | null =
                            discount > 0 ? `+$${freePlayAmount} Free Play!` : `+$${freePlayAmount} Free Play!`;

                        if (discount > 0 && !freePlayAmount) {
                            discountText = `Get $${discountAmount} More!`;
                        } else if (!freePlayAmount) {
                            discountText = null;
                        }

                        const freePlayText: string | null = freePlayAmount > 0 ? `$${freePlayAmount} Free Play` : null;

                        const packageText = freePlayAmount
                            ? `at Select Florida Hard Rock Casinos <br/>+ $${wdollarParams.amount} Wdollars`
                            : `$${wdollarParams.amount} Wdollars for Just $${price}`;

                        packages.push({
                            id: item.offerId,
                            price,
                            package: wdollarParams.amount,
                            discountText: discountText,
                            ticket: response.data.ticket,
                            freePlay: freePlayAmount,
                            text: packageText,
                            freePlayText: freePlayText,
                        });
                    }
                }

                packages.sort((a, b) => {
                    return +a.package > +b.package ? 1 : -1;
                });
                const wdollarPackages = plainToInstance(ModelWdollarPackage, packages);
                this.setWdollarPackages(wdollarPackages);

                authService.sendGaEvent({
                    category: GaCategories.purchaseFlow,
                    action: GaEvents.storeViewed,
                    label: JSON.stringify({
                        Goldoffers_visible: 3,
                        balance_wdollars: authService.currentUser.balance,
                        balance_freeplay: authService.currentUser.freePlay,
                    }),
                });
            }
        } catch (error: any) {
            if (error.response && error.response.status && error.response.status === 403) {
                authService.setLastError(extractServerError(error?.response?.data ?? error));
                authService.setShowForbiddenPopup(true);
            } else {
                this.setLastError(extractServerError(error?.response?.data ?? error));
            }
        }
    };

    createOrder: PayPalButtonsComponentProps['createOrder'] = async () => {
        try {
            /*const url: string = `${process.env.BACKEND_HOST}${EApiUrl.order}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
              cart: [{ id: '1', quantity: '1' }],
          }),
        });
        
        const orderData: any = await response.json();
        if (!orderData.id) {
          const errorDetail = orderData?.details[0];
          const errorMessage = errorDetail
              ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
              : 'Unexpected error occurred, please try again.';

          throw new Error(errorMessage);
        }

        return orderData.id;*/
            const orderData: any = {
                id: '10',
            };
            if (!orderData.id) {
                const errorDetail = orderData?.details[0];
                const errorMessage = errorDetail
                    ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
                    : 'Unexpected error occurred, please try again.';

                throw new Error(errorMessage);
            }

            return orderData.id;
        } catch (error) {
            console.error(error);
        }
    };

    paypalOnApprove: PayPalButtonsComponentProps['onApprove'] = async data => {
        const url = `${process.env.BACKEND_HOST}${EApiUrl.order}`;
        // Capture the funds from the transaction.
        const response = await fetch(url, {
            method: 'PUT',
            body: JSON.stringify({
                orderID: data.orderID,
            }),
        });

        const details = await response.json();
    };

    @action
    handleSlideChange = (swiper: any) => {
        if (swiper.isBeginning) {
            this.slideArrowLeftEnabled = false;
        } else {
            this.slideArrowLeftEnabled = true;
        }

        if (swiper.isEnd) {
            this.slideArrowRightEnabled = false;
        } else {
            this.slideArrowRightEnabled = true;
        }
    };

    @action
    getWdollarPackage = (wdollarPackageId: number) => {
        this.selectedWdollarPackage = this.wdollarPackages.find(
            item => item.id === +wdollarPackageId,
        ) as ModelWdollarPackage;

        ReactGA.event({
            category: GaCategories.purchaseFlow,
            action: GaEvents.productSelected,
            label: JSON.stringify({
                item_id: this.selectedWdollarPackage.id,
                value: this.selectedWdollarPackage.package,
                price_usd: this.selectedWdollarPackage.price,
                freeplay_bonus: `$${this.selectedWdollarPackage.freePlay}`,
            }),
        });
    };

    onAfterPayment = async (paymentData: any, token: string | undefined) => {
        try {
            this.order = {
                id: paymentData.id,
                date: moment().utc().format('DD MMM, YYYY'),
                paymentMethod: paymentData.paymentMethod,
                total: paymentData.total,
                packagePrice: paymentData.packagePrice,
                packageFreePlay: paymentData.packageFreePlay,
            };
            this.setShowSuccessBlock(true);
        } catch (error: any) {}
    };

    @action
    getPublicWdollarPackages = async () => {
        try {
            const response: AxiosResponse = await api.get<ModelWdollarPackage[]>(EApiUrl.publicWdollarPackages);
            if (response.status === 200) {
                const packages = [];
                for (const item of response.data.bundles[0].offers) {
                    const wdollar = item.items.find((record: OfferItem) => record.typ === 'wdollar');
                    if (wdollar && wdollar.parameters) {
                        const wdollarParams = JSON.parse(wdollar.parameters);
                        packages.push({
                            id: item.offer_id,
                            price: null,
                            package: wdollarParams.amount,
                            discountText: 'Get Much More! + Free Play',
                            text: 'Get an amazing discount and Free Play at casino when you purchase WDollars!',
                        });
                    }
                }

                packages.sort((a, b) => {
                    return +a.package > +b.package ? 1 : -1;
                });
                const wdollarPackages = plainToInstance(ModelWdollarPackage, packages);
                this.setWdollarPackages(wdollarPackages);
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
        }
    };

    @action
    setLastError = (err: { description: string; code?: string }) => {
        this.lastError = err;
    };

    @action
    clearLastError = () => {
        this.lastError = { description: '', code: undefined };
    };
}
